import GridTable from '../../table/GridTable';
import {
  ColDef,
  ICellRendererParams,
  RowClassParams,
} from 'ag-grid-community';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { AgGridReact } from 'ag-grid-react';
import queryString from 'query-string';
import { IPurchaseOrder, IPurchaseOrderApprovalRequest } from '../../../type/purchaseOrder';
import { usePurchaseOrderStore } from '../../../store/usePurchaseOrderStore';
import PurchaseOrderFilter from './filter/PurchaseOrderFilter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useLoginStore } from '../../../store/useLogin';

/**
 * @author assistant
 * @date 2025.01.05
 * @description 복비 입금대기 승인/거절 관리 페이지
 */
const PurchaseOrderList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRows, setSelectedRows] = useState<IPurchaseOrder[]>([]);
  const [showMemoModal, setShowMemoModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [memo, setMemo] = useState('');
  const { role } = useLoginStore();
  const isAdmin = role === 'ROLE_ADMIN';

  const baseActionButtonClass =
    '!px-4 !py-2 !text-white !font-medium !rounded-lg !text-sm !transition-colors !duration-200';
  const disabledButtonClass =
    'disabled:!bg-gray-300 disabled:!text-gray-600 disabled:!cursor-not-allowed';

  // 기본 날짜 범위 (2주)
  const getDefaultDateRange = () => {
    const today = new Date();
    const twoWeeksAgo = new Date();
    twoWeeksAgo.setDate(today.getDate() - 14);

    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      start: formatDate(twoWeeksAgo),
      end: formatDate(today)
    };
  };
  const defaultDates = getDefaultDateRange();

  const {
    purchaseOrders,
    purchaseOrderPage,
    getPurchaseOrdersLoading,
    approvePurchaseOrdersLoading,
    downloadPurchaseOrdersLoading,
    approvedAmountSum,
    getApprovedAmountSumLoading,
    getPurchaseOrders,
    downloadPurchaseOrdersExcel,
    approvePurchaseOrders,
    rejectPurchaseOrders,
    getApprovedAmountSum,
  } = usePurchaseOrderStore();

  const gridRef = useRef<AgGridReact<IPurchaseOrder>>(null);

  const colDefs: ColDef<IPurchaseOrder>[] = [
    ...(isAdmin ? [{
      headerName: '',
      field: 'id' as keyof IPurchaseOrder,
      checkboxSelection: (params: ICellRendererParams) => true,
      headerCheckboxSelection: true,
      width: 50,
      maxWidth: 50,
      minWidth: 50,
    } as ColDef<IPurchaseOrder>] : []),
    {
      headerName: '상태',
      field: 'chargeType',
      cellRenderer: (props: ICellRendererParams) => {
        const statusMap = {
          'WAIT': { text: '대기', className: 'bg-blue-100 text-blue-800' },
          'SUCCESS': { text: '승인', className: 'bg-green-100 text-green-800' },
          'REJECT': { text: '거절', className: 'bg-red-100 text-red-800' },
        };
        const status = statusMap[props.value as keyof typeof statusMap];
        return status ? (
          <span className={`px-2 py-1 rounded text-xs font-medium ${status.className}`}>
            {status.text}
          </span>
        ) : null;
      },
      flex: 1.5,
    },
    {
      headerName: '가맹점명',
      field: 'companyName',
      flex: 2,
    },
    {
      headerName: '입금자명',
      field: 'depositName',
      flex: 1.5,
    },
    {
      headerName: '입금은행',
      field: 'depositBank',
      flex: 1.5,
    },
    {
      headerName: '입금금액',
      field: 'depositAmount',
      flex: 1.5,
      valueFormatter: params => {
        const value = Number(params.value);
        return isNaN(value) ? params.value : value.toLocaleString() + '원';
      },
    },
    {
      headerName: '사업자번호',
      field: 'companyNumber',
      flex: 1.5,
    },
    {
      headerName: '대표자',
      field: 'ceo',
      flex: 1,
    },
    {
      headerName: '이메일',
      field: 'email',
      flex: 2,
    },
    {
      headerName: '주소',
      field: 'address',
      flex: 3,
    },
    {
      headerName: '등록일',
      field: 'createdAt',
      valueFormatter: param => formatDate(param.value).timestamp,
      flex: 2,
    },
    {
      headerName: '승인/거절 사유',
      field: 'description',
      flex: 3,
    },
  ];

  const getFilteredData = () => {
    const parsedQuery = queryString.parse(location.search);
    const purchaseType = (parsedQuery.purchaseType as 'WAIT' | 'SUCCESS' | 'REJECT') || '';
    const franchiseId = (parsedQuery.franchiseId as string) || '';
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;
    const StartDt = (parsedQuery.StartDt as string) || '';
    const EndDt = (parsedQuery.EndDt as string) || '';
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;

    const queryParams = {
      purchaseType,
      franchiseId,
      size,
      StartDt,
      EndDt,
      page,
    };

    updateUrlWithParams(queryParams, navigate);
    getPurchaseOrders({
      purchaseType,
      franchiseId,
      startDt: StartDt,
      endDt: EndDt,
      page,
      size,
    });

    // 승인 합계도 함께 조회
    if (StartDt && EndDt) {
      getApprovedAmountSum(StartDt, EndDt);
    }
  };

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const purchaseType = (parsedQuery.purchaseType as 'WAIT' | 'SUCCESS' | 'REJECT') || '';
    const franchiseId = (parsedQuery.franchiseId as string) || '';
    const StartDt = (parsedQuery.StartDt as string) || defaultDates.start;
    const EndDt = (parsedQuery.EndDt as string) || defaultDates.end;
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;

    getPurchaseOrders({
      purchaseType,
      franchiseId,
      startDt: StartDt,
      endDt: EndDt,
      page,
      size,
    });

    // 승인 합계도 함께 조회
    getApprovedAmountSum(StartDt, EndDt);
  }, [getPurchaseOrders, getApprovedAmountSum, location.search]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const updatedQuery = { ...parsedQuery, page: page.toString() };

      updateUrlWithParams(updatedQuery, navigate);
      getPurchaseOrders({
        page,
        purchaseType: (parsedQuery.purchaseType as 'WAIT' | 'SUCCESS' | 'REJECT') || '',
        size: parseInt(parsedQuery.size as string) || 20,
        franchiseId: (parsedQuery.franchiseId as string) || '',
        startDt: (parsedQuery.StartDt as string) || '',
        endDt: (parsedQuery.EndDt as string) || ''
      });
    },
    [getPurchaseOrders, location.search, navigate]
  );

  const onSelectionChanged = () => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes();
    const selectedData = selectedNodes?.map(node => node.data) || [];
    setSelectedRows(selectedData as IPurchaseOrder[]);
  };

  const handleApprove = () => {
    if (selectedRows.length === 0) {
      toast.warning('승인할 항목을 선택해주세요.');
      return;
    }
    setActionType('approve');
    setShowMemoModal(true);
  };

  const handleReject = () => {
    if (selectedRows.length === 0) {
      toast.warning('거절할 항목을 선택해주세요.');
      return;
    }
    setActionType('reject');
    setShowMemoModal(true);
  };

  const handleMemoSubmit = async () => {
    const request: IPurchaseOrderApprovalRequest = {
      purchaseOrderIds: selectedRows.map(row => row.id),
      action: actionType === 'approve' ? 'APPROVE' : 'REJECT',
    };

    try {
      let success = false;
      if (actionType === 'approve') {
        success = await approvePurchaseOrders(request, memo || '');
      } else {
        success = await rejectPurchaseOrders(request, memo || '');
      }

      if (success) {
        setSelectedRows([]);
        setShowMemoModal(false);
        setMemo('');
        getFilteredData();
      } else {
        toast.error(`입금 ${actionType === 'approve' ? '승인' : '거절'}에 실패했습니다.`);
      }
    } catch (error) {
      console.error(`입금 ${actionType === 'approve' ? '승인' : '거절'} 처리 중 오류:`, error);
      toast.error(`입금 ${actionType === 'approve' ? '승인' : '거절'} 처리 중 오류가 발생했습니다.`);
    }
  };

  const handleDownloadExcel = useCallback(() => {
    const parsedQuery = queryString.parse(location.search);
    const purchaseType =
      (parsedQuery.purchaseType as 'WAIT' | 'SUCCESS' | 'REJECT') || '';
    const franchiseId = (parsedQuery.franchiseId as string) || '';
    const StartDt = (parsedQuery.StartDt as string) || '';
    const EndDt = (parsedQuery.EndDt as string) || '';

    downloadPurchaseOrdersExcel({
      purchaseType: purchaseType
        ? (purchaseType as 'WAIT' | 'SUCCESS' | 'REJECT')
        : undefined,
      franchiseId: franchiseId || undefined,
      startDt: StartDt || undefined,
      endDt: EndDt || undefined,
    });
  }, [downloadPurchaseOrdersExcel, location.search]);

  const handleModalClose = () => {
    setShowMemoModal(false);
    setMemo('');
  };

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !flex-wrap !items-center !justify-between !gap-3 !py-4">
        <div className="!flex !items-center !gap-3">
          <PurchaseOrderFilter getFilteredData={getFilteredData} />
          {isAdmin && approvedAmountSum !== null && (
            <div className="!flex !items-center !gap-2 !px-3 !py-2 !bg-green-50 !border !border-green-200 !rounded-lg !whitespace-nowrap">
              <span className="!text-sm !text-green-700 !font-medium">승인 합계:</span>
              <span className="!text-sm !text-green-800 !font-bold">
                {getApprovedAmountSumLoading ? '조회 중...' : `${approvedAmountSum.toLocaleString()}원`}
              </span>
            </div>
          )}
        </div>
        <div className="!flex !flex-wrap !items-center !gap-2">
          {isAdmin && (
            <button
              type="button"
              onClick={handleDownloadExcel}
              disabled={downloadPurchaseOrdersLoading}
              className={`${baseActionButtonClass} ${disabledButtonClass} !bg-blue-500 hover:!bg-blue-600`}
            >
              {downloadPurchaseOrdersLoading ? '다운로드 중...' : '엑셀 다운로드'}
            </button>
          )}
          {isAdmin && (
            <div className="!flex !flex-wrap !items-center !gap-2">
              {selectedRows.length > 0 && (
                <span className="!text-sm !text-gray-600 !self-center">
                  {selectedRows.length}개 선택됨
                </span>
              )}
              <button
                type="button"
                onClick={handleApprove}
                className={`${baseActionButtonClass} !bg-blue-500 hover:!bg-blue-600`}
              >
                입금 승인
              </button>
              <button
                type="button"
                onClick={handleReject}
                className={`${baseActionButtonClass} !bg-red-500 hover:!bg-red-600`}
              >
                입금 거절
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IPurchaseOrder>
          gridRef={gridRef}
          height="100%"
          isLoading={getPurchaseOrdersLoading}
          rowData={purchaseOrders}
          columnDefs={colDefs}
          rowSelection="multiple"
          onSelectionChanged={onSelectionChanged}
          rowClassRules={{
            wait_row: (params: RowClassParams) =>
              params.data.chargeType === 'WAIT',
            success_row: (params: RowClassParams) =>
              params.data.chargeType === 'SUCCESS',
            reject_row: (params: RowClassParams) =>
              params.data.chargeType === 'REJECT',
          }}
        />
      </div>

      {/* 페이지네이션 */}
      {purchaseOrderPage && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-1">
            {/* 맨 처음으로 */}
            <button
              onClick={() => onPageChange(0)}
              disabled={purchaseOrderPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3 !-ml-1" />
            </button>
            
            {/* 이전 페이지 */}
            <button
              onClick={() => onPageChange(Math.max(0, purchaseOrderPage.pageable.pageNumber - 1))}
              disabled={purchaseOrderPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
            </button>
            
            {/* 페이지 번호들 */}
            <div className="!flex !space-x-0.5">
              {(() => {
                const currentPage = purchaseOrderPage.pageable.pageNumber;
                const totalPages = purchaseOrderPage.totalPages;
                let startPage = Math.max(0, currentPage - 4);
                let endPage = Math.min(totalPages - 1, startPage + 9);
                
                if (endPage - startPage < 9) {
                  startPage = Math.max(0, endPage - 9);
                }
                
                return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                      pageNum === currentPage
                        ? '!bg-blue-600 !text-white'
                        : 'hover:!bg-gray-100 !text-gray-700'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                ));
              })()}
            </div>
            
            {/* 다음 페이지 */}
            <button
              onClick={() => onPageChange(Math.min(purchaseOrderPage.totalPages - 1, purchaseOrderPage.pageable.pageNumber + 1))}
              disabled={purchaseOrderPage.pageable.pageNumber === purchaseOrderPage.totalPages - 1}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
            </button>
            
            {/* 맨 마지막으로 */}
            <button
              onClick={() => onPageChange(purchaseOrderPage.totalPages - 1)}
              disabled={purchaseOrderPage.pageable.pageNumber === purchaseOrderPage.totalPages - 1}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3 !-ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* 사유 입력 모달 */}
      {showMemoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-lg mx-4">
            <h3 className="text-lg font-semibold mb-4">
              {actionType === 'approve' ? '승인' : '거절'} 사유 입력
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                사유 (선택사항)
              </label>
              <textarea
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="사유를 입력해주세요..."
                className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                maxLength={500}
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {memo.length}/500
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleModalClose}
                className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleMemoSubmit}
                disabled={approvePurchaseOrdersLoading}
                className={`px-4 py-2 text-white rounded-md transition-colors ${
                  actionType === 'approve' 
                    ? 'bg-green-500 hover:bg-green-600' 
                    : 'bg-red-500 hover:bg-red-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {approvePurchaseOrdersLoading ? '처리중...' : 
                  actionType === 'approve' ? '승인' : '거절'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderList;
